import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Building2, Plus, X, Search, Edit2, Download, BarChart2,
  Settings, CheckCircle, ChevronRight, FileText, DollarSign,
  Layers, Calendar, Users, MapPin, Shield, Activity,
  AlertTriangle, TrendingUp, Trash2, RefreshCw
} from 'lucide-react';

const BLDG_TYPES = [
  {code:'IH',label:'Independent House'},{code:'VI',label:'Villa'},
  {code:'SB',label:'Single Block Apt'},{code:'MB',label:'Multi Block Apt'},
  {code:'HR',label:'High Rise'},{code:'BP',label:'Build & Plot'},{code:'TW',label:'Townhouse'},
];
const STATUSES = ['ENQUIRY','ESTIMATION','AGREEMENT','IN_PROGRESS','COMPLETED','HANDOVER','ON_HOLD','CANCELLED'];
const STATUS_FLOW: Record<string,string[]> = {
  ENQUIRY:    ['ESTIMATION','ON_HOLD','CANCELLED'],
  ESTIMATION: ['AGREEMENT','ENQUIRY','ON_HOLD','CANCELLED'],
  AGREEMENT:  ['IN_PROGRESS','ESTIMATION','ON_HOLD','CANCELLED'],
  IN_PROGRESS:['COMPLETED','ON_HOLD','CANCELLED'],
  COMPLETED:  ['HANDOVER'],
  HANDOVER:   [],
  ON_HOLD:    ['IN_PROGRESS','CANCELLED'],
  CANCELLED:  [],
};
const STATUS_COLORS: Record<string,string> = {
  ENQUIRY:'bg-gray-100 text-gray-600', ESTIMATION:'bg-blue-100 text-blue-700',
  AGREEMENT:'bg-indigo-100 text-indigo-700', IN_PROGRESS:'bg-green-100 text-green-700',
  COMPLETED:'bg-emerald-100 text-emerald-700', HANDOVER:'bg-teal-100 text-teal-700',
  ON_HOLD:'bg-amber-100 text-amber-700', CANCELLED:'bg-red-100 text-red-700',
};
const BLDG_COLORS: Record<string,string> = {
  IH:'bg-blue-500',VI:'bg-purple-500',SB:'bg-cyan-500',
  MB:'bg-indigo-500',HR:'bg-rose-500',BP:'bg-amber-500',TW:'bg-green-500',
};
const BUDGET_CATS = ['CIVIL_STRUCTURE','MEP','FINISHING','EXTERNAL','MACHINERY','OVERHEAD','PROFESSIONAL_FEES','CONTINGENCY','OTHER'];
const FLOOR_TYPES = ['FLOOR','BASEMENT','TERRACE','EXTERNAL','COMMON','PODIUM'];
const HEALTH_COLORS: Record<string,string> = {
  HEALTHY:'text-green-600 bg-green-50', AT_RISK:'text-amber-600 bg-amber-50', CRITICAL:'text-red-600 bg-red-50'
};

const inp   = "w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500";
const inpRO = "w-full border border-gray-200 rounded-lg px-3 py-2 text-sm bg-gray-50 text-gray-400 cursor-not-allowed";
const fmtDate = (d?: string) => d ? new Date(d).toLocaleDateString('en-IN') : 'â€”';
const fmtN = (n: any) => n ? Number(n).toLocaleString() : '0';
const fmtCr = (n: any) => {
  if (!n || Number(n)===0) return 'â€”';
  const v = Number(n);
  if (v>=10000000) return `â‚¹${(v/10000000).toFixed(2)} Cr`;
  return `â‚¹${(v/100000).toFixed(2)} L`;
};
const exportCSV = (data: any[], filename: string) => {
  if (!data.length) return;
  const keys = Object.keys(data[0]);
  const csv = [keys.join(','), ...data.map(row =>
    keys.map(k=>`"${String(row[k]??'').replace(/"/g,'""')}"`).join(','))].join('\n');
  const a = document.createElement('a');
  a.href = URL.createObjectURL(new Blob([csv],{type:'text/csv'}));
  a.download = filename; a.click();
};

// â”€â”€ Status Change Modal â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
const StatusModal = ({ project, onClose, onSaved }: { project:any; onClose:()=>void; onSaved:()=>void }) => {
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState(project.status);
  const nextStatuses = STATUS_FLOW[project.status] || [];

  const submit = async (e: React.FormEvent) => {
    e.preventDefault(); setSaving(true);
    try {
      const { default: api } = await import('../../utils/api');
      await api.patch(`/projects/${project.project_id}/status`, { status });
      onSaved();
    } finally { setSaving(false); }
  };
  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl w-full max-w-sm shadow-xl">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <div><h3 className="font-bold text-gray-900">Update Status</h3>
            <p className="text-xs text-gray-400">{project.project_name}</p></div>
          <button onClick={onClose}><X size={18} className="text-gray-400"/></button>
        </div>
        <form onSubmit={submit} className="p-6 space-y-3">
          <div className="mb-2">
            <p className="text-xs text-gray-500 mb-2">Current: <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_COLORS[project.status]}`}>{project.status}</span></p>
            {nextStatuses.length === 0
              ? <p className="text-sm text-gray-400 italic">No further status transitions available.</p>
              : <div className="grid grid-cols-2 gap-2">
                  {STATUSES.filter(s => nextStatuses.includes(s) || s === project.status).map(s => (
                    <button key={s} type="button" onClick={() => setStatus(s)}
                      className={`px-3 py-2.5 rounded-lg text-xs font-medium border-2 transition-all
                        ${status===s ? 'border-blue-600 bg-blue-50 text-blue-700' : 'border-gray-200 text-gray-600 hover:border-gray-300'}`}>
                      {s.replace('_',' ')}
                    </button>
                  ))}
                </div>}
          </div>
          <div className="flex gap-3 pt-1">
            <button type="button" onClick={onClose}
              className="flex-1 px-4 py-2 border border-gray-200 text-gray-600 text-sm rounded-lg hover:bg-gray-50">Cancel</button>
            <button type="submit" disabled={saving || status===project.status || nextStatuses.length===0}
              className="flex-1 px-4 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 disabled:opacity-50">
              {saving ? 'Updating...' : 'Update Status'}</button>
          </div>
        </form>
      </div>
    </div>
  );
};

// â”€â”€ Add/Edit Project Modal â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
const ProjectModal = ({ project, onClose, onSaved }: { project?:any; onClose:()=>void; onSaved:()=>void }) => {
  const isEdit = !!project;
  const [saving, setSaving]   = useState(false);
  const [error, setError]     = useState('');
  const [subtab, setSubtab]   = useState<'basic'|'site'|'team'>('basic');
  const [customers, setCustomers] = useState<any[]>([]);
  const [users, setUsers]     = useState<any[]>([]);
  const [form, setForm] = useState({
    project_name:       project?.project_name        || '',
    customer_id:        project?.customer_id         || '',
    building_type:      project?.building_type       || 'IH',
    site_address:       project?.site_address        || '',
    site_city:          project?.site_city           || 'Bengaluru',
    site_gps_lat:       project?.site_gps_lat        || '12.9716',
    site_gps_lng:       project?.site_gps_lng        || '77.5946',
    site_gps_radius_m:  project?.site_gps_radius_m   || 50,
    site_area_sqft:     project?.site_area_sqft      || '',
    built_up_area_sqft: project?.built_up_area_sqft  || '',
    number_of_floors:   project?.number_of_floors    || 1,
    number_of_blocks:   project?.number_of_blocks    || 1,
    number_of_units:    project?.number_of_units     || '',
    status:             project?.status              || 'ENQUIRY',
    contract_value:     project?.contract_value      || '',
    contract_date:      project?.contract_date?.split('T')[0]        || '',
    start_date:         project?.start_date?.split('T')[0]           || '',
    expected_end_date:  project?.expected_end_date?.split('T')[0]    || '',
    project_manager_id: project?.project_manager_id  || '',
    site_engineer_id:   project?.site_engineer_id    || '',
    bbmp_plan_no:       project?.bbmp_plan_no        || '',
    rera_no:            project?.rera_no             || '',
    is_rera_applicable: project?.is_rera_applicable  || false,
    is_active:          project?.is_active !== undefined ? project.is_active : true,
  });
  const set = (k:string,v:any) => setForm(f=>({...f,[k]:v}));

  useEffect(() => {
    const load = async () => {
      const { default: api } = await import('../../utils/api');
      const [cr, ur] = await Promise.all([
        api.get('/customers', { params:{ is_active:true } }),
        api.get('/users'),
      ]);
      setCustomers(cr.data.data||[]); setUsers(ur.data.data||[]);
    };
    load();
  }, []);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault(); setSaving(true); setError('');
    try {
      const { default: api } = await import('../../utils/api');
      const payload = {
        ...form,
        customer_id: Number(form.customer_id),
        project_manager_id: form.project_manager_id ? Number(form.project_manager_id) : null,
        site_engineer_id:   form.site_engineer_id   ? Number(form.site_engineer_id)   : null,
        contract_value:     form.contract_value     ? Number(form.contract_value)     : null,
        site_area_sqft:     form.site_area_sqft     ? Number(form.site_area_sqft)     : null,
        built_up_area_sqft: form.built_up_area_sqft ? Number(form.built_up_area_sqft) : null,
        number_of_units:    form.number_of_units    ? Number(form.number_of_units)    : null,
      };
      if (isEdit) await api.put(`/projects/${project.project_id}`, payload);
      else        await api.post('/projects/', payload);
      onSaved();
    } catch(err:any) { setError(err.response?.data?.message||'Failed'); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl w-full max-w-xl shadow-xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 sticky top-0 bg-white">
          <div>
            <h3 className="font-bold text-gray-900">{isEdit ? 'Edit Project' : 'New Project'}</h3>
            {isEdit && <p className="text-xs text-gray-400">{project.project_code}</p>}
          </div>
          <button onClick={onClose}><X size={18} className="text-gray-400"/></button>
        </div>
        <div className="flex gap-1 px-6 pt-4">
          {(['basic','site','team'] as const).map(t=>(
            <button key={t} type="button" onClick={()=>setSubtab(t)}
              className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-colors
                ${subtab===t?'bg-blue-600 text-white':'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>
              {t==='basic'?'Basic Info':t==='site'?'Site Details':'Team & Contract'}</button>
          ))}
        </div>
        <form onSubmit={submit} className="p-6 space-y-3">
          {error && <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg px-3 py-2 text-sm">{error}</div>}

          {subtab==='basic' && <>
            {!isEdit && <div className="px-3 py-2 bg-blue-50 border border-blue-200 rounded-lg text-xs text-blue-700">Code auto-generated e.g. RBD-IH-26-001</div>}
            {isEdit && <div><label className="text-xs font-medium text-gray-600 block mb-1">Project Code</label><input value={project.project_code} readOnly className={inpRO}/></div>}
            <div><label className="text-xs font-medium text-gray-600 block mb-1">Project Name *</label>
              <input required value={form.project_name} onChange={e=>set('project_name',e.target.value)} placeholder="Sharma Residence, Whitefield" className={inp}/></div>
            <div className="grid grid-cols-2 gap-3">
              <div><label className="text-xs font-medium text-gray-600 block mb-1">Customer *</label>
                <select required value={form.customer_id} onChange={e=>set('customer_id',e.target.value)} className={inp}>
                  <option value="">Select customer</option>
                  {customers.map(c=><option key={c.customer_id} value={c.customer_id}>{c.full_name} ({c.customer_code})</option>)}
                </select></div>
              <div><label className="text-xs font-medium text-gray-600 block mb-1">Building Type *</label>
                <select required value={form.building_type} onChange={e=>set('building_type',e.target.value)} className={inp}>
                  {BLDG_TYPES.map(b=><option key={b.code} value={b.code}>{b.label}</option>)}
                </select></div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div><label className="text-xs font-medium text-gray-600 block mb-1">Floors</label>
                <input type="number" min={1} value={form.number_of_floors} onChange={e=>set('number_of_floors',Number(e.target.value))} className={inp}/></div>
              <div><label className="text-xs font-medium text-gray-600 block mb-1">Blocks</label>
                <input type="number" min={1} value={form.number_of_blocks} onChange={e=>set('number_of_blocks',Number(e.target.value))} className={inp}/></div>
              <div><label className="text-xs font-medium text-gray-600 block mb-1">Units</label>
                <input type="number" value={form.number_of_units} onChange={e=>set('number_of_units',e.target.value)} placeholder="Optional" className={inp}/></div>
            </div>
            {isEdit && (
              <div className="grid grid-cols-2 gap-3">
                <div><label className="text-xs font-medium text-gray-600 block mb-1">Status</label>
                  <select value={form.status} onChange={e=>set('status',e.target.value)} className={inp}>
                    {STATUSES.map(s=><option key={s}>{s}</option>)}</select></div>
                <div className="flex items-end pb-2">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input type="checkbox" checked={form.is_active} onChange={e=>set('is_active',e.target.checked)} className="w-4 h-4 rounded border-gray-300 text-blue-600"/>
                    <span className="text-sm text-gray-700">Active</span></label></div>
              </div>
            )}
          </>}

          {subtab==='site' && <>
            <div><label className="text-xs font-medium text-gray-600 block mb-1">Site Address *</label>
              <textarea rows={2} required value={form.site_address} onChange={e=>set('site_address',e.target.value)} className={inp}/></div>
            <div className="grid grid-cols-2 gap-3">
              <div><label className="text-xs font-medium text-gray-600 block mb-1">City</label>
                <input value={form.site_city} onChange={e=>set('site_city',e.target.value)} className={inp}/></div>
              <div><label className="text-xs font-medium text-gray-600 block mb-1">GPS Radius (m)</label>
                <input type="number" value={form.site_gps_radius_m} onChange={e=>set('site_gps_radius_m',Number(e.target.value))} className={inp}/></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><label className="text-xs font-medium text-gray-600 block mb-1">GPS Latitude</label>
                <input type="number" step="0.0000001" value={form.site_gps_lat} onChange={e=>set('site_gps_lat',e.target.value)} className={inp}/></div>
              <div><label className="text-xs font-medium text-gray-600 block mb-1">GPS Longitude</label>
                <input type="number" step="0.0000001" value={form.site_gps_lng} onChange={e=>set('site_gps_lng',e.target.value)} className={inp}/></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><label className="text-xs font-medium text-gray-600 block mb-1">Site Area (SFT)</label>
                <input type="number" value={form.site_area_sqft} onChange={e=>set('site_area_sqft',e.target.value)} className={inp}/></div>
              <div><label className="text-xs font-medium text-gray-600 block mb-1">Built-up Area (SFT)</label>
                <input type="number" value={form.built_up_area_sqft} onChange={e=>set('built_up_area_sqft',e.target.value)} className={inp}/></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><label className="text-xs font-medium text-gray-600 block mb-1">BBMP Plan No</label>
                <input value={form.bbmp_plan_no} onChange={e=>set('bbmp_plan_no',e.target.value)} className={inp}/></div>
              <div><label className="text-xs font-medium text-gray-600 block mb-1">RERA No</label>
                <input value={form.rera_no} onChange={e=>set('rera_no',e.target.value)} className={inp}/></div>
            </div>
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={form.is_rera_applicable} onChange={e=>set('is_rera_applicable',e.target.checked)} className="w-4 h-4 rounded border-gray-300 text-blue-600"/>
              <span className="text-sm text-gray-700">RERA Applicable</span></label>
          </>}

          {subtab==='team' && <>
            <div className="grid grid-cols-2 gap-3">
              <div><label className="text-xs font-medium text-gray-600 block mb-1">Project Manager</label>
                <select value={form.project_manager_id} onChange={e=>set('project_manager_id',e.target.value)} className={inp}>
                  <option value="">Select PM</option>
                  {users.map(u=><option key={u.user_id} value={u.user_id}>{u.full_name||u.username}</option>)}
                </select></div>
              <div><label className="text-xs font-medium text-gray-600 block mb-1">Site Engineer</label>
                <select value={form.site_engineer_id} onChange={e=>set('site_engineer_id',e.target.value)} className={inp}>
                  <option value="">Select SE</option>
                  {users.map(u=><option key={u.user_id} value={u.user_id}>{u.full_name||u.username}</option>)}
                </select></div>
            </div>
            <div><label className="text-xs font-medium text-gray-600 block mb-1">Contract Value (â‚¹)</label>
              <input type="number" value={form.contract_value} onChange={e=>set('contract_value',e.target.value)} className={inp}/></div>
            <div className="grid grid-cols-3 gap-3">
              <div><label className="text-xs font-medium text-gray-600 block mb-1">Contract Date</label>
                <input type="date" value={form.contract_date} onChange={e=>set('contract_date',e.target.value)} className={inp}/></div>
              <div><label className="text-xs font-medium text-gray-600 block mb-1">Start Date</label>
                <input type="date" value={form.start_date} onChange={e=>set('start_date',e.target.value)} className={inp}/></div>
              <div><label className="text-xs font-medium text-gray-600 block mb-1">Expected End</label>
                <input type="date" value={form.expected_end_date} onChange={e=>set('expected_end_date',e.target.value)} className={inp}/></div>
            </div>
          </>}

          <div className="flex gap-3 pt-2">
            <button type="button" onClick={onClose} className="flex-1 px-4 py-2 border border-gray-200 text-gray-600 text-sm rounded-lg hover:bg-gray-50">Cancel</button>
            <button type="submit" disabled={saving} className="flex-1 px-4 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 disabled:opacity-50">
              {saving ? 'Saving...' : (isEdit ? 'Update Project' : 'Create Project')}</button>
          </div>
        </form>
      </div>
    </div>
  );
};

// â”€â”€ Project Detail Modal â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
const ProjectDetailModal = ({ projectId, onClose, onRefresh }: { projectId:number; onClose:()=>void; onRefresh:()=>void }) => {
  const [dashboard, setDashboard] = useState<any>(null);
  const [loading, setLoading]     = useState(true);
  const [tab, setTab]             = useState<'overview'|'blocks'|'budget'|'template'|'docs'|'health'>('overview');
  const [blocks, setBlocks]       = useState<any[]>([]);
  const [floors, setFloors]       = useState<any[]>([]);
  const [budget, setBudget]       = useState<any[]>([]);
  const [budgetSum, setBudgetSum] = useState<any>(null);
  const [template, setTemplate]   = useState<any[]>([]);
  const [docs, setDocs]           = useState<any[]>([]);
  const [health, setHealth]       = useState<any[]>([]);
  const [generating, setGenerating] = useState(false);
  const [error, setError]         = useState('');

  // Add block form
  const [showAddBlock, setShowAddBlock] = useState(false);
  const [blockForm, setBlockForm] = useState({ block_code:'', block_name:'', floors_count:1, units_per_floor:'', start_date:'', expected_end_date:'' });

  // Add floor form
  const [showAddFloor, setShowAddFloor] = useState(false);
  const [floorForm, setFloorForm] = useState({ block_id:'', floor_code:'', floor_name:'', floor_type:'FLOOR', area_sqft:'', sequence_order:0 });

  // Add budget form
  const [showAddBudget, setShowAddBudget] = useState(false);
  const [budgetForm, setBudgetForm] = useState({ cost_category:'CIVIL_STRUCTURE', budgeted_amount:'', revised_amount:'', committed_amount:0, spent_amount:0 });

  // Add document form
  const [showAddDoc, setShowAddDoc] = useState(false);
  const [docForm, setDocForm] = useState({ doc_name:'', doc_type:'DRAWING', cloud_url:'', expiry_date:'' });

  // Health score form
  const [showAddHealth, setShowAddHealth] = useState(false);
  const [healthForm, setHealthForm] = useState({ score_date: new Date().toISOString().split('T')[0], schedule_score:0, cost_score:0, quality_score:0, safety_score:0, health_status:'HEALTHY', notes:'' });

  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const { default: api } = await import('../../utils/api');
        const r = await api.get(`/projects/${projectId}/dashboard`);
        setDashboard(r.data.data);
      } finally { setLoading(false); }
    };
    load();
  }, [projectId]);

  const loadTab = async (t: string) => {
    const { default: api } = await import('../../utils/api');
    try {
      if (t==='blocks') {
        const [br, fr] = await Promise.all([
          api.get(`/projects/${projectId}/blocks`),
          api.get(`/projects/${projectId}/floors`)
        ]);
        setBlocks(br.data.data); setFloors(fr.data.data);
      }
      if (t==='budget')   { const r = await api.get(`/projects/${projectId}/budget`);    setBudget(r.data.data); setBudgetSum(r.data.summary); }
      if (t==='template') { const r = await api.get(`/projects/${projectId}/template`);  setTemplate(r.data.data); }
      if (t==='docs')     { const r = await api.get(`/projects/${projectId}/documents`); setDocs(r.data.data); }
      if (t==='health')   { const r = await api.get(`/projects/${projectId}/health`);    setHealth(r.data.data); }
    } catch(e:any) { setError(e.response?.data?.message||'Load failed'); }
  };

  useEffect(() => { if (tab!=='overview') loadTab(tab); }, [tab]);

  const apiPost = async (url:string, data:any, refreshTab:string) => {
    setSaving(true); setError('');
    try {
      const { default: api } = await import('../../utils/api');
      await api.post(url, data);
      loadTab(refreshTab);
      return true;
    } catch(e:any) { setError(e.response?.data?.message||'Failed'); return false; }
    finally { setSaving(false); }
  };

  const saveBlock = async (e: React.FormEvent) => {
    e.preventDefault();
    const ok = await apiPost(`/projects/${projectId}/blocks`, {
      ...blockForm, floors_count:Number(blockForm.floors_count),
      units_per_floor: blockForm.units_per_floor ? Number(blockForm.units_per_floor) : null,
    }, 'blocks');
    if (ok) setShowAddBlock(false);
  };

  const saveFloor = async (e: React.FormEvent) => {
    e.preventDefault();
    const ok = await apiPost(`/projects/${projectId}/floors`, {
      ...floorForm, block_id: floorForm.block_id ? Number(floorForm.block_id) : null,
      area_sqft: floorForm.area_sqft ? Number(floorForm.area_sqft) : null,
    }, 'blocks');
    if (ok) setShowAddFloor(false);
  };

  const saveBudget = async (e: React.FormEvent) => {
    e.preventDefault();
    const ok = await apiPost(`/projects/${projectId}/budget`, budgetForm, 'budget');
    if (ok) setShowAddBudget(false);
  };

  const saveDoc = async (e: React.FormEvent) => {
    e.preventDefault();
    const ok = await apiPost(`/projects/${projectId}/documents`, docForm, 'docs');
    if (ok) setShowAddDoc(false);
  };

  const saveHealth = async (e: React.FormEvent) => {
    e.preventDefault();
    const ok = await apiPost(`/projects/${projectId}/health`, healthForm, 'health');
    if (ok) setShowAddHealth(false);
  };

  const deleteDoc = async (docId: number) => {
    if (!window.confirm('Remove this document?')) return;
    const { default: api } = await import('../../utils/api');
    await api.delete(`/projects/${projectId}/documents/${docId}`);
    loadTab('docs');
  };

  const generateTemplate = async () => {
    setGenerating(true); setError('');
    try {
      const { default: api } = await import('../../utils/api');
      const r = await api.post(`/projects/${projectId}/template/generate`);
      loadTab('template');
      alert(r.data.message);
    } catch(e:any) { setError(e.response?.data?.message||'Failed'); }
    finally { setGenerating(false); }
  };

  const approveTemplate = async () => {
    const { default: api } = await import('../../utils/api');
    await api.post(`/projects/${projectId}/template/approve`);
    loadTab('template');
  };

  const lockTemplate = async () => {
    if (!window.confirm('Lock template? This cannot be undone.')) return;
    const { default: api } = await import('../../utils/api');
    await api.post(`/projects/${projectId}/template/lock`);
    loadTab('template');
  };

  if (loading) return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
      <div className="bg-white rounded-2xl w-full max-w-4xl p-8 text-center text-gray-400">Loading dashboard...</div>
    </div>
  );

  const p = dashboard?.project;
  const sch = dashboard?.schedule_summary;
  const bgt = dashboard?.budget_summary;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl w-full max-w-4xl shadow-2xl max-h-[92vh] flex flex-col">
        {/* Header */}
        <div className="flex items-start justify-between px-6 py-4 border-b border-gray-200 flex-shrink-0">
          <div className="flex items-center gap-3">
            <div className={`w-10 h-10 rounded-xl flex items-center justify-center text-white text-sm font-bold ${BLDG_COLORS[p?.building_type]||'bg-gray-500'}`}>
              {p?.building_type}</div>
            <div>
              <h3 className="font-bold text-gray-900 text-lg">{p?.project_name}</h3>
              <p className="text-xs text-gray-400">{p?.project_code} Â· {p?.site_city} Â· {p?.customer_name}</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <span className={`text-xs px-3 py-1 rounded-full font-medium ${STATUS_COLORS[p?.status]||''}`}>{p?.status}</span>
            {p?.contract_value && <span className="text-sm font-bold text-gray-700">{fmtCr(p.contract_value)}</span>}
            <button onClick={onClose}><X size={18} className="text-gray-400"/></button>
          </div>
        </div>

        {/* Sub-tabs */}
        <div className="flex gap-1 px-6 pt-3 border-b border-gray-100 flex-shrink-0 overflow-x-auto">
          {[
            {key:'overview',label:'Overview',    icon:<Activity size={12}/>},
            {key:'blocks',  label:'Blocks/Floors',icon:<Layers size={12}/>},
            {key:'budget',  label:'Budget',      icon:<DollarSign size={12}/>},
            {key:'template',label:'Activities',  icon:<Settings size={12}/>},
            {key:'docs',    label:'Documents',   icon:<FileText size={12}/>},
            {key:'health',  label:'Health Score',icon:<Shield size={12}/>},
          ].map((t:any) => (
            <button key={t.key} onClick={()=>setTab(t.key)}
              className={`flex items-center gap-1.5 px-4 py-2 text-xs font-medium rounded-t-lg transition-colors whitespace-nowrap
                ${tab===t.key?'bg-blue-600 text-white':'text-gray-500 hover:text-gray-700'}`}>
              {t.icon}{t.label}</button>
          ))}
        </div>

        {error && <div className="mx-6 mt-2 bg-red-50 border border-red-200 text-red-700 rounded-lg px-3 py-2 text-sm flex-shrink-0">{error}</div>}

        <div className="flex-1 overflow-y-auto p-6">
          {/* â”€â”€ OVERVIEW â”€â”€ */}
          {tab==='overview' && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {[
                  {label:'Progress',       val:`${sch?.overall_pct||0}%`,                   color:'bg-blue-600 text-white'},
                  {label:'Done/Total',     val:`${fmtN(sch?.completed)}/${fmtN(sch?.total_activities)}`, color:'bg-green-600 text-white'},
                  {label:'Delayed',        val:fmtN(sch?.delayed),                          color:'bg-red-500 text-white'},
                  {label:'Contract Value', val:fmtCr(p?.contract_value),                    color:'bg-indigo-600 text-white'},
                ].map(s=>(
                  <div key={s.label} className={`${s.color} rounded-xl p-3 text-center`}>
                    <p className="text-xl font-bold">{s.val}</p>
                    <p className="text-xs mt-0.5 opacity-90">{s.label}</p>
                  </div>
                ))}
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-gray-50 rounded-xl p-4">
                  <h4 className="text-xs font-bold text-gray-600 mb-3 flex items-center gap-1"><Building2 size={12}/>Project Details</h4>
                  {[
                    {label:'Customer',     val:p?.customer_name},
                    {label:'Type',         val:BLDG_TYPES.find(b=>b.code===p?.building_type)?.label},
                    {label:'Floors',       val:`${p?.number_of_floors}`},
                    {label:'Start Date',   val:fmtDate(p?.start_date)},
                    {label:'Expected End', val:fmtDate(p?.expected_end_date)},
                    {label:'BBMP Plan',    val:p?.bbmp_plan_no},
                    {label:'RERA',         val:p?.rera_no||( p?.is_rera_applicable ? 'Applicable' : 'N/A')},
                  ].map(s=>(
                    <div key={s.label} className="flex justify-between text-sm mb-1">
                      <span className="text-gray-500">{s.label}</span>
                      <span className="font-medium text-gray-800 text-right">{s.val||'â€”'}</span>
                    </div>
                  ))}
                </div>
                <div className="bg-gray-50 rounded-xl p-4">
                  <h4 className="text-xs font-bold text-gray-600 mb-3 flex items-center gap-1"><Users size={12}/>Team & Site</h4>
                  {[
                    {label:'Project Manager', val:p?.project_manager_name},
                    {label:'Site Engineer',   val:p?.site_engineer_name},
                    {label:'Address',         val:p?.site_address},
                    {label:'Built-up',        val:p?.built_up_area_sqft ? `${fmtN(p.built_up_area_sqft)} SFT` : 'â€”'},
                    {label:'GPS Radius',      val:`${p?.site_gps_radius_m}m`},
                    {label:'GPS',             val:`${p?.site_gps_lat}, ${p?.site_gps_lng}`},
                  ].map(s=>(
                    <div key={s.label} className="flex justify-between text-sm mb-1">
                      <span className="text-gray-500">{s.label}</span>
                      <span className="font-medium text-gray-800 text-right max-w-[60%] truncate">{s.val||'â€”'}</span>
                    </div>
                  ))}
                </div>
              </div>
              {sch && Number(sch.total_activities)>0 && (
                <div className="bg-white rounded-xl border border-gray-200 p-4">
                  <h4 className="text-xs font-bold text-gray-600 mb-3">Activity Status</h4>
                  {[
                    {label:'Completed',  val:sch.completed,   color:'bg-green-500'},
                    {label:'In Progress',val:sch.in_progress, color:'bg-blue-500'},
                    {label:'Delayed',    val:sch.delayed,     color:'bg-red-500'},
                    {label:'Overdue',    val:sch.overdue,     color:'bg-rose-600'},
                  ].map(s=>(
                    <div key={s.label} className="flex items-center gap-3 mb-1.5">
                      <span className="text-xs text-gray-600 w-24">{s.label}</span>
                      <div className="flex-1 bg-gray-100 rounded-full h-2">
                        <div className={`${s.color} h-2 rounded-full`} style={{width:`${(Number(s.val)/Number(sch.total_activities))*100}%`}}/>
                      </div>
                      <span className="text-xs font-semibold text-gray-700 w-8 text-right">{fmtN(s.val)}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* â”€â”€ BLOCKS & FLOORS â”€â”€ */}
          {tab==='blocks' && (
            <div className="space-y-4">
              <div className="flex gap-2">
                <button onClick={()=>setShowAddBlock(!showAddBlock)}
                  className="flex items-center gap-1 text-xs bg-blue-600 text-white px-3 py-1.5 rounded-lg hover:bg-blue-700">
                  <Plus size={12}/> Add Block</button>
                <button onClick={()=>setShowAddFloor(!showAddFloor)}
                  className="flex items-center gap-1 text-xs bg-indigo-600 text-white px-3 py-1.5 rounded-lg hover:bg-indigo-700">
                  <Plus size={12}/> Add Floor</button>
              </div>

              {showAddBlock && (
                <form onSubmit={saveBlock} className="bg-blue-50 border border-blue-200 rounded-xl p-4 space-y-3">
                  <h4 className="text-xs font-bold text-blue-700">New Block</h4>
                  <div className="grid grid-cols-2 gap-3">
                    <div><label className="text-xs text-gray-600 block mb-1">Block Code *</label>
                      <input required value={blockForm.block_code} onChange={e=>setBlockForm(f=>({...f,block_code:e.target.value}))} placeholder="A, B, Block-1" className={inp}/></div>
                    <div><label className="text-xs text-gray-600 block mb-1">Block Name *</label>
                      <input required value={blockForm.block_name} onChange={e=>setBlockForm(f=>({...f,block_name:e.target.value}))} placeholder="Block A" className={inp}/></div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div><label className="text-xs text-gray-600 block mb-1">Floors Count</label>
                      <input type="number" min={1} value={blockForm.floors_count} onChange={e=>setBlockForm(f=>({...f,floors_count:Number(e.target.value)}))} className={inp}/></div>
                    <div><label className="text-xs text-gray-600 block mb-1">Units per Floor</label>
                      <input type="number" value={blockForm.units_per_floor} onChange={e=>setBlockForm(f=>({...f,units_per_floor:e.target.value}))} className={inp}/></div>
                  </div>
                  <div className="flex gap-2 justify-end">
                    <button type="button" onClick={()=>setShowAddBlock(false)} className="px-3 py-1.5 text-xs border border-gray-200 rounded-lg">Cancel</button>
                    <button type="submit" disabled={saving} className="px-3 py-1.5 text-xs bg-blue-600 text-white rounded-lg disabled:opacity-50">{saving?'Saving...':'Save Block'}</button>
                  </div>
                </form>
              )}

              {showAddFloor && (
                <form onSubmit={saveFloor} className="bg-indigo-50 border border-indigo-200 rounded-xl p-4 space-y-3">
                  <h4 className="text-xs font-bold text-indigo-700">New Floor</h4>
                  <div className="grid grid-cols-3 gap-3">
                    <div><label className="text-xs text-gray-600 block mb-1">Block</label>
                      <select value={floorForm.block_id} onChange={e=>setFloorForm(f=>({...f,block_id:e.target.value}))} className={inp}>
                        <option value="">No block</option>
                        {blocks.map(b=><option key={b.block_id} value={b.block_id}>{b.block_name}</option>)}
                      </select></div>
                    <div><label className="text-xs text-gray-600 block mb-1">Floor Code *</label>
                      <input required value={floorForm.floor_code} onChange={e=>setFloorForm(f=>({...f,floor_code:e.target.value}))} placeholder="F00,F01" className={inp}/></div>
                    <div><label className="text-xs text-gray-600 block mb-1">Floor Name *</label>
                      <input required value={floorForm.floor_name} onChange={e=>setFloorForm(f=>({...f,floor_name:e.target.value}))} placeholder="Ground Floor" className={inp}/></div>
                  </div>
                  <div className="grid grid-cols-3 gap-3">
                    <div><label className="text-xs text-gray-600 block mb-1">Type</label>
                      <select value={floorForm.floor_type} onChange={e=>setFloorForm(f=>({...f,floor_type:e.target.value}))} className={inp}>
                        {FLOOR_TYPES.map(t=><option key={t}>{t}</option>)}</select></div>
                    <div><label className="text-xs text-gray-600 block mb-1">Area (SFT)</label>
                      <input type="number" value={floorForm.area_sqft} onChange={e=>setFloorForm(f=>({...f,area_sqft:e.target.value}))} className={inp}/></div>
                    <div><label className="text-xs text-gray-600 block mb-1">Sequence</label>
                      <input type="number" value={floorForm.sequence_order} onChange={e=>setFloorForm(f=>({...f,sequence_order:Number(e.target.value)}))} className={inp}/></div>
                  </div>
                  <div className="flex gap-2 justify-end">
                    <button type="button" onClick={()=>setShowAddFloor(false)} className="px-3 py-1.5 text-xs border border-gray-200 rounded-lg">Cancel</button>
                    <button type="submit" disabled={saving} className="px-3 py-1.5 text-xs bg-indigo-600 text-white rounded-lg disabled:opacity-50">{saving?'Saving...':'Save Floor'}</button>
                  </div>
                </form>
              )}

              {blocks.map(b=>(
                <div key={b.block_id} className="bg-white rounded-xl border border-gray-200 p-4">
                  <div className="flex items-center justify-between mb-2">
                    <div>
                      <p className="font-semibold text-gray-900">{b.block_name}</p>
                      <p className="text-xs text-gray-400">{b.block_code} Â· {b.floors_count} floors {b.total_units ? `Â· ${b.total_units} units` : ''}</p>
                    </div>
                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium
                      ${b.status==='COMPLETED'?'bg-green-100 text-green-700':b.status==='IN_PROGRESS'?'bg-blue-100 text-blue-700':'bg-gray-100 text-gray-600'}`}>
                      {b.status}</span>
                  </div>
                  {/* Floors for this block */}
                  {floors.filter(f=>f.block_id===b.block_id).length>0 && (
                    <div className="flex flex-wrap gap-1.5 mt-2">
                      {floors.filter(f=>f.block_id===b.block_id).map(f=>(
                        <span key={f.floor_id} className="text-xs bg-gray-50 border border-gray-200 px-2 py-0.5 rounded text-gray-600">
                          {f.floor_name} {f.area_sqft ? `(${fmtN(f.area_sqft)} SFT)` : ''}</span>
                      ))}
                    </div>
                  )}
                </div>
              ))}
              {blocks.length===0 && <div className="text-center py-8 text-gray-400">No blocks yet. Add a block to start.</div>}
            </div>
          )}

          {/* â”€â”€ BUDGET â”€â”€ */}
          {tab==='budget' && (
            <div className="space-y-4">
              {budgetSum && (
                <div className="grid grid-cols-4 gap-3">
                  {[
                    {label:'Budget',   val:fmtCr(budgetSum.total_budget),   color:'bg-blue-50 text-blue-700'},
                    {label:'Revised',  val:fmtCr(budgetSum.total_revised),  color:'bg-indigo-50 text-indigo-700'},
                    {label:'Spent',    val:fmtCr(budgetSum.total_spent),    color:'bg-amber-50 text-amber-700'},
                    {label:'Variance', val:fmtCr(budgetSum.total_variance), color:`${Number(budgetSum.total_variance)>=0?'bg-green-50 text-green-700':'bg-red-50 text-red-700'}`},
                  ].map(s=>(
                    <div key={s.label} className={`${s.color} rounded-xl p-3 text-center`}>
                      <p className="text-lg font-bold">{s.val}</p><p className="text-xs mt-0.5">{s.label}</p>
                    </div>
                  ))}
                </div>
              )}
              <button onClick={()=>setShowAddBudget(!showAddBudget)}
                className="flex items-center gap-1 text-xs bg-blue-600 text-white px-3 py-1.5 rounded-lg hover:bg-blue-700">
                <Plus size={12}/> Add/Update Budget Line</button>

              {showAddBudget && (
                <form onSubmit={saveBudget} className="bg-blue-50 border border-blue-200 rounded-xl p-4 space-y-3">
                  <div className="grid grid-cols-2 gap-3">
                    <div><label className="text-xs text-gray-600 block mb-1">Cost Category *</label>
                      <select value={budgetForm.cost_category} onChange={e=>setBudgetForm(f=>({...f,cost_category:e.target.value}))} className={inp}>
                        {BUDGET_CATS.map(c=><option key={c}>{c}</option>)}</select></div>
                    <div><label className="text-xs text-gray-600 block mb-1">Budgeted Amount (â‚¹) *</label>
                      <input type="number" required value={budgetForm.budgeted_amount} onChange={e=>setBudgetForm(f=>({...f,budgeted_amount:e.target.value}))} className={inp}/></div>
                  </div>
                  <div className="grid grid-cols-3 gap-3">
                    <div><label className="text-xs text-gray-600 block mb-1">Revised Amount (â‚¹)</label>
                      <input type="number" value={budgetForm.revised_amount} onChange={e=>setBudgetForm(f=>({...f,revised_amount:e.target.value}))} className={inp}/></div>
                    <div><label className="text-xs text-gray-600 block mb-1">Committed (â‚¹)</label>
                      <input type="number" value={budgetForm.committed_amount} onChange={e=>setBudgetForm(f=>({...f,committed_amount:Number(e.target.value)}))} className={inp}/></div>
                    <div><label className="text-xs text-gray-600 block mb-1">Spent (â‚¹)</label>
                      <input type="number" value={budgetForm.spent_amount} onChange={e=>setBudgetForm(f=>({...f,spent_amount:Number(e.target.value)}))} className={inp}/></div>
                  </div>
                  <div className="flex gap-2 justify-end">
                    <button type="button" onClick={()=>setShowAddBudget(false)} className="px-3 py-1.5 text-xs border border-gray-200 rounded-lg">Cancel</button>
                    <button type="submit" disabled={saving} className="px-3 py-1.5 text-xs bg-blue-600 text-white rounded-lg disabled:opacity-50">{saving?'Saving...':'Save Budget Line'}</button>
                  </div>
                </form>
              )}

              <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 border-b border-gray-200">
                    <tr>
                      <th className="text-left px-4 py-2 text-xs font-medium text-gray-500">Category</th>
                      <th className="text-right px-4 py-2 text-xs font-medium text-gray-500">Budget</th>
                      <th className="text-right px-4 py-2 text-xs font-medium text-gray-500">Revised</th>
                      <th className="text-right px-4 py-2 text-xs font-medium text-gray-500">Committed</th>
                      <th className="text-right px-4 py-2 text-xs font-medium text-gray-500">Spent</th>
                      <th className="text-right px-4 py-2 text-xs font-medium text-gray-500">Variance</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {budget.length===0 ? (
                      <tr><td colSpan={6} className="text-center py-8 text-gray-400">No budget set.</td></tr>
                    ) : budget.map(b=>(
                      <tr key={b.budget_id} className="hover:bg-gray-50">
                        <td className="px-4 py-2 font-medium text-gray-800 text-xs">{b.cost_category.replace(/_/g,' ')}</td>
                        <td className="px-4 py-2 text-right text-xs">{fmtCr(b.budgeted_amount)}</td>
                        <td className="px-4 py-2 text-right text-xs text-gray-500">{fmtCr(b.revised_amount)}</td>
                        <td className="px-4 py-2 text-right text-xs text-indigo-600">{fmtCr(b.committed_amount)}</td>
                        <td className="px-4 py-2 text-right text-xs text-amber-600 font-medium">{fmtCr(b.spent_amount)}</td>
                        <td className={`px-4 py-2 text-right text-xs font-semibold ${Number(b.variance)>=0?'text-green-600':'text-red-600'}`}>{fmtCr(b.variance)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* â”€â”€ TEMPLATE â”€â”€ */}
          {tab==='template' && (
            <div className="space-y-3">
              <div className="flex items-center gap-2 flex-wrap">
                <p className="text-sm text-gray-500">{template.length} activities</p>
                <button onClick={generateTemplate} disabled={generating}
                  className="flex items-center gap-1 px-3 py-1.5 text-xs bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50">
                  <RefreshCw size={12}/> {generating?'Generating...':(template.length>0?'Regenerate':'Generate from Master')}</button>
                {template.length>0 && template.some((t:any)=>t.template_status==='DRAFT') && (
                  <button onClick={approveTemplate}
                    className="flex items-center gap-1 px-3 py-1.5 text-xs bg-green-600 text-white rounded-lg hover:bg-green-700">
                    <CheckCircle size={12}/> Approve All</button>
                )}
                {template.length>0 && template.every((t:any)=>t.template_status==='APPROVED') && (
                  <button onClick={lockTemplate}
                    className="flex items-center gap-1 px-3 py-1.5 text-xs bg-amber-600 text-white rounded-lg hover:bg-amber-700">
                    ðŸ”’ Lock Template</button>
                )}
              </div>
              {template.length===0 ? (
                <div className="text-center py-12 text-gray-400">
                  <Settings size={40} className="mx-auto mb-3 opacity-30"/>
                  <p>No template yet. Click Generate to load all 2015 activities.</p>
                </div>
              ) : (
                <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                  <div className="max-h-96 overflow-y-auto">
                    <table className="w-full text-xs">
                      <thead className="bg-gray-50 border-b border-gray-200 sticky top-0">
                        <tr>
                          <th className="text-left px-3 py-2 font-medium text-gray-500">Code</th>
                          <th className="text-left px-3 py-2 font-medium text-gray-500">Activity</th>
                          <th className="text-left px-3 py-2 font-medium text-gray-500">Stage</th>
                          <th className="text-left px-3 py-2 font-medium text-gray-500">Type</th>
                          <th className="text-left px-3 py-2 font-medium text-gray-500">Status</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {template.map((t:any)=>(
                          <tr key={t.template_id} className="hover:bg-gray-50">
                            <td className="px-3 py-1.5 font-mono text-gray-400">{t.act_code}</td>
                            <td className="px-3 py-1.5 text-gray-800">{t.activity_name}</td>
                            <td className="px-3 py-1.5 text-gray-500 text-xs">{t.stage_name}</td>
                            <td className="px-3 py-1.5">
                              {t.activity_type && <span className="px-1.5 py-0.5 rounded text-xs bg-gray-100 text-gray-600">{t.activity_type}</span>}
                            </td>
                            <td className="px-3 py-1.5">
                              <span className={`px-2 py-0.5 rounded-full font-medium text-xs
                                ${t.template_status==='LOCKED'?'bg-red-100 text-red-700':
                                  t.template_status==='APPROVED'?'bg-green-100 text-green-700':'bg-gray-100 text-gray-600'}`}>
                                {t.template_status}</span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* â”€â”€ DOCUMENTS â”€â”€ */}
          {tab==='docs' && (
            <div className="space-y-3">
              <button onClick={()=>setShowAddDoc(!showAddDoc)}
                className="flex items-center gap-1 text-xs bg-blue-600 text-white px-3 py-1.5 rounded-lg hover:bg-blue-700">
                <Plus size={12}/> Add Document</button>

              {showAddDoc && (
                <form onSubmit={saveDoc} className="bg-blue-50 border border-blue-200 rounded-xl p-4 space-y-3">
                  <div className="grid grid-cols-2 gap-3">
                    <div><label className="text-xs text-gray-600 block mb-1">Document Name *</label>
                      <input required value={docForm.doc_name} onChange={e=>setDocForm(f=>({...f,doc_name:e.target.value}))} placeholder="Structural Drawing Rev2" className={inp}/></div>
                    <div><label className="text-xs text-gray-600 block mb-1">Document Type *</label>
                      <input required value={docForm.doc_type} onChange={e=>setDocForm(f=>({...f,doc_type:e.target.value}))} placeholder="DRAWING/APPROVAL/CONTRACT" className={inp}/></div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div><label className="text-xs text-gray-600 block mb-1">URL / Path</label>
                      <input value={docForm.cloud_url} onChange={e=>setDocForm(f=>({...f,cloud_url:e.target.value}))} placeholder="https://..." className={inp}/></div>
                    <div><label className="text-xs text-gray-600 block mb-1">Expiry Date</label>
                      <input type="date" value={docForm.expiry_date} onChange={e=>setDocForm(f=>({...f,expiry_date:e.target.value}))} className={inp}/></div>
                  </div>
                  <div className="flex gap-2 justify-end">
                    <button type="button" onClick={()=>setShowAddDoc(false)} className="px-3 py-1.5 text-xs border border-gray-200 rounded-lg">Cancel</button>
                    <button type="submit" disabled={saving} className="px-3 py-1.5 text-xs bg-blue-600 text-white rounded-lg disabled:opacity-50">{saving?'Saving...':'Add Document'}</button>
                  </div>
                </form>
              )}

              {docs.length===0 ? <div className="text-center py-8 text-gray-400">No documents yet.</div>
              : docs.map(d=>(
                <div key={d.doc_id} className="flex items-center justify-between bg-white rounded-xl border border-gray-200 px-4 py-3">
                  <div>
                    <p className="text-sm font-medium text-gray-900">{d.doc_name}</p>
                    <p className="text-xs text-gray-400">{d.doc_type} Â· v{d.version_number} Â· {d.uploaded_by_name} Â· {fmtDate(d.uploaded_at)}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    {d.expiry_date && <span className="text-xs text-amber-600">Expires: {fmtDate(d.expiry_date)}</span>}
                    {d.cloud_url && <a href={d.cloud_url} target="_blank" rel="noreferrer" className="text-xs text-blue-600 hover:underline">View</a>}
                    {d.is_latest && <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full">Latest</span>}
                    <button onClick={()=>deleteDoc(d.doc_id)} className="text-red-400 hover:text-red-600"><Trash2 size={13}/></button>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* â”€â”€ HEALTH SCORE â”€â”€ */}
          {tab==='health' && (
            <div className="space-y-4">
              <button onClick={()=>setShowAddHealth(!showAddHealth)}
                className="flex items-center gap-1 text-xs bg-blue-600 text-white px-3 py-1.5 rounded-lg hover:bg-blue-700">
                <Plus size={12}/> Add Health Score</button>

              {showAddHealth && (
                <form onSubmit={saveHealth} className="bg-blue-50 border border-blue-200 rounded-xl p-4 space-y-3">
                  <div className="grid grid-cols-2 gap-3">
                    <div><label className="text-xs text-gray-600 block mb-1">Score Date</label>
                      <input type="date" value={healthForm.score_date} onChange={e=>setHealthForm(f=>({...f,score_date:e.target.value}))} className={inp}/></div>
                    <div><label className="text-xs text-gray-600 block mb-1">Health Status</label>
                      <select value={healthForm.health_status} onChange={e=>setHealthForm(f=>({...f,health_status:e.target.value}))} className={inp}>
                        {['HEALTHY','AT_RISK','CRITICAL'].map(s=><option key={s}>{s}</option>)}</select></div>
                  </div>
                  <div className="grid grid-cols-4 gap-3">
                    {[
                      {key:'schedule_score',label:'Schedule (0-100)'},
                      {key:'cost_score',label:'Cost (0-100)'},
                      {key:'quality_score',label:'Quality (0-100)'},
                      {key:'safety_score',label:'Safety (0-100)'},
                    ].map(s=>(
                      <div key={s.key}><label className="text-xs text-gray-600 block mb-1">{s.label}</label>
                        <input type="number" min={0} max={100} value={(healthForm as any)[s.key]}
                          onChange={e=>setHealthForm(f=>({...f,[s.key]:Number(e.target.value)}))} className={inp}/></div>
                    ))}
                  </div>
                  <div><label className="text-xs text-gray-600 block mb-1">Notes</label>
                    <textarea rows={2} value={healthForm.notes} onChange={e=>setHealthForm(f=>({...f,notes:e.target.value}))} className={inp}/></div>
                  <div className="flex gap-2 justify-end">
                    <button type="button" onClick={()=>setShowAddHealth(false)} className="px-3 py-1.5 text-xs border border-gray-200 rounded-lg">Cancel</button>
                    <button type="submit" disabled={saving} className="px-3 py-1.5 text-xs bg-blue-600 text-white rounded-lg disabled:opacity-50">{saving?'Saving...':'Save Score'}</button>
                  </div>
                </form>
              )}

              {health.length===0 ? <div className="text-center py-8 text-gray-400">No health scores recorded.</div>
              : health.map(h=>(
                <div key={h.health_id} className="bg-white rounded-xl border border-gray-200 p-4">
                  <div className="flex items-center justify-between mb-3">
                    <p className="text-sm font-semibold text-gray-700">{fmtDate(h.score_date)}</p>
                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${HEALTH_COLORS[h.health_status]||''}`}>
                      {h.health_status}</span>
                  </div>
                  <div className="grid grid-cols-5 gap-3 text-center">
                    {[
                      {label:'Schedule', val:h.schedule_score, color:'bg-blue-500'},
                      {label:'Cost',     val:h.cost_score,     color:'bg-green-500'},
                      {label:'Quality',  val:h.quality_score,  color:'bg-purple-500'},
                      {label:'Safety',   val:h.safety_score,   color:'bg-amber-500'},
                      {label:'Overall',  val:h.overall_score,  color:'bg-indigo-600'},
                    ].map(s=>(
                      <div key={s.label}>
                        <div className={`w-12 h-12 mx-auto rounded-full flex items-center justify-center text-white text-sm font-bold mb-1 ${s.color}`}>
                          <span className="text-sm font-bold">{Math.round(Number(s.val))}</span>
                        </div>
                        <p className="text-xs text-gray-500">{s.label}</p>
                      </div>
                    ))}
                  </div>
                  {h.notes && <p className="text-xs text-gray-500 mt-2">{h.notes}</p>}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// â”€â”€ Projects List Tab â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
const ProjectsListTab = () => {
  const [projects, setProjects]     = useState<any[]>([]);
  const [stats, setStats]           = useState<any>(null);
  const [loading, setLoading]       = useState(true);
  const [statusFilter, setStatus]   = useState('');
  const [typeFilter, setType]       = useState('');
  const [search, setSearch]         = useState('');
  const [showAdd, setShowAdd]       = useState(false);
  const [editProject, setEditProject] = useState<any>(null);
  const [detailId, setDetailId]     = useState<number|null>(null);
  const [statusModal, setStatusModal] = useState<any>(null);

  const load = async () => {
    setLoading(true);
    try {
      const { default: api } = await import('../../utils/api');
      const [pr, sr] = await Promise.all([
        api.get('/projects/', { params:{ status:statusFilter||undefined, building_type:typeFilter||undefined, search:search||undefined }}),
        api.get('/projects/stats')
      ]);
      setProjects(pr.data.data); setStats(sr.data.data);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [statusFilter, typeFilter]);

  return (
    <>
      {showAdd && <ProjectModal onClose={()=>setShowAdd(false)} onSaved={()=>{ setShowAdd(false); load(); }}/>}
      {editProject && <ProjectModal project={editProject} onClose={()=>setEditProject(null)} onSaved={()=>{ setEditProject(null); load(); }}/>}
      {detailId && <ProjectDetailModal projectId={detailId} onClose={()=>setDetailId(null)} onRefresh={load}/>}
      {statusModal && <StatusModal project={statusModal} onClose={()=>setStatusModal(null)} onSaved={()=>{ setStatusModal(null); load(); }}/>}

      <div className="flex items-center justify-between mb-4">
        <p className="text-sm text-gray-500">{projects.length} projects</p>
        <div className="flex gap-2">
          <button onClick={()=>exportCSV(projects.map(p=>({
            Code:p.project_code,Name:p.project_name,Customer:p.customer_name,
            Type:p.building_type,Status:p.status,Floors:p.number_of_floors,
            ContractValue:p.contract_value||'',StartDate:fmtDate(p.start_date),
            ExpectedEnd:fmtDate(p.expected_end_date),PM:p.project_manager_name||''
          })),'projects.csv')}
            className="flex items-center gap-1 px-3 py-2 bg-gray-100 text-gray-700 text-sm rounded-lg hover:bg-gray-200">
            <Download size={14}/> Export</button>
          <button onClick={()=>setShowAdd(true)}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700">
            <Plus size={15}/> New Project</button>
        </div>
      </div>

      {/* Stats bar */}
      {stats && (
        <div className="grid grid-cols-4 md:grid-cols-8 gap-2 mb-4">
          {[
            {label:'Enquiry',    val:stats.enquiry,    s:'ENQUIRY'},
            {label:'Estimation', val:stats.estimation, s:'ESTIMATION'},
            {label:'Agreement',  val:stats.agreement,  s:'AGREEMENT'},
            {label:'In Progress',val:stats.in_progress,s:'IN_PROGRESS'},
            {label:'Completed',  val:stats.completed,  s:'COMPLETED'},
            {label:'Handover',   val:stats.handover,   s:'HANDOVER'},
            {label:'On Hold',    val:stats.on_hold,    s:'ON_HOLD'},
            {label:'Total',      val:stats.total,      s:''},
          ].map(s=>(
            <div key={s.label} onClick={()=>setStatus(statusFilter===s.s?'':s.s)}
              className={`rounded-xl p-2 text-center cursor-pointer transition-all
                ${statusFilter===s.s?'ring-2 ring-blue-500':''}
                ${s.s===''?'bg-gray-800 text-white':STATUS_COLORS[s.s]||'bg-gray-50 text-gray-700'}`}>
              <p className="text-lg font-bold">{fmtN(s.val)}</p>
              <p className="text-xs mt-0.5 opacity-80">{s.label}</p>
            </div>
          ))}
        </div>
      )}

      {/* Type filter */}
      <div className="flex flex-wrap gap-2 mb-3">
        {[{code:'',label:'All Types'},...BLDG_TYPES].map(b=>(
          <button key={b.code} onClick={()=>setType(b.code)}
            className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors
              ${typeFilter===b.code?'bg-purple-600 text-white border-purple-600':'bg-white text-gray-600 border-gray-200'}`}>
            {b.label}</button>
        ))}
      </div>

      {/* Search */}
      <form onSubmit={e=>{e.preventDefault();load();}} className="flex gap-2 mb-4">
        <div className="relative flex-1 max-w-sm">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"/>
          <input value={search} onChange={e=>setSearch(e.target.value)} placeholder="Search project, customer..."
            className="w-full pl-9 pr-4 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"/>
        </div>
        <button type="submit" className="px-4 py-2 bg-gray-100 text-gray-700 text-sm rounded-lg hover:bg-gray-200">Search</button>
      </form>

      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {[...Array(6)].map((_,i)=>(
            <div key={i} className="bg-white rounded-xl border border-gray-200 p-4 animate-pulse">
              <div className="h-5 bg-gray-100 rounded w-3/4 mb-2"/><div className="h-4 bg-gray-100 rounded w-1/2"/>
            </div>
          ))}
        </div>
      ) : projects.length===0 ? (
        <div className="text-center py-16 text-gray-400">
          <Building2 size={40} className="mx-auto mb-3 opacity-30"/>
          <p>No projects found.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {projects.map(p=>(
            <div key={p.project_id} className="bg-white rounded-xl border border-gray-200 p-4 hover:shadow-sm transition-shadow">
              <div className="flex items-start justify-between mb-2">
                <div className="flex items-center gap-2 cursor-pointer" onClick={()=>setDetailId(p.project_id)}>
                  <div className={`w-9 h-9 rounded-xl flex items-center justify-center text-white text-xs font-bold ${BLDG_COLORS[p.building_type]||'bg-gray-500'}`}>
                    {p.building_type}</div>
                  <div>
                    <p className="font-semibold text-gray-900 text-sm">{p.project_name}</p>
                    <p className="text-xs text-gray-400">{p.project_code}</p>
                  </div>
                </div>
                {/* Status badge â€” clickable */}
                <button onClick={()=>setStatusModal(p)}
                  className={`text-xs px-2 py-0.5 rounded-full font-medium cursor-pointer hover:opacity-80 transition-opacity ${STATUS_COLORS[p.status]||''}`}>
                  {p.status.replace('_',' ')} âœ
                </button>
              </div>
              <p className="text-xs text-gray-500 mb-2">{p.customer_name} Â· {p.site_city}</p>
              <div className="flex flex-wrap gap-1.5 mb-3">
                <span className="text-xs bg-gray-50 border border-gray-200 text-gray-600 px-2 py-0.5 rounded">{p.number_of_floors}F</span>
                {p.contract_value && <span className="text-xs bg-green-50 border border-green-200 text-green-700 px-2 py-0.5 rounded">{fmtCr(p.contract_value)}</span>}
                {p.activity_count>0 && <span className="text-xs bg-blue-50 border border-blue-200 text-blue-700 px-2 py-0.5 rounded">{fmtN(p.activity_count)} act</span>}
                {p.start_date && <span className="text-xs bg-gray-50 border border-gray-200 text-gray-500 px-2 py-0.5 rounded">{fmtDate(p.start_date)}</span>}
              </div>
              {p.overall_progress!==null && (
                <div className="mb-3">
                  <div className="flex justify-between text-xs text-gray-400 mb-1">
                    <span>Progress</span><span>{p.overall_progress||0}%</span>
                  </div>
                  <div className="bg-gray-100 rounded-full h-1.5">
                    <div className="bg-blue-500 h-1.5 rounded-full" style={{width:`${p.overall_progress||0}%`}}/>
                  </div>
                </div>
              )}
              <div className="flex items-center justify-between">
                <p className="text-xs text-gray-400">{p.project_manager_name ? `PM: ${p.project_manager_name}` : 'No PM'}</p>
                <div className="flex gap-1">
                  <button onClick={()=>setEditProject(p)} className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg" title="Edit"><Edit2 size={13}/></button>
                  <a href={`/projects/${p.project_id}/plan`} onClick={e=>e.stopPropagation()} className="flex items-center gap-1 px-2 py-1 text-xs bg-blue-600 text-white rounded-lg hover:bg-blue-700">Plan</a>
                  
                    
                  <button onClick={()=>setDetailId(p.project_id)} className="p-1.5 text-gray-400 hover:text-gray-700 hover:bg-gray-100 rounded-lg" title="Details"><ChevronRight size={13}/></button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </>
  );
};

// â”€â”€ Stats Tab â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
const StatsTab = () => {
  const [stats, setStats] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    const load = async () => {
      const { default: api } = await import('../../utils/api');
      const r = await api.get('/projects/stats');
      setStats(r.data.data); setLoading(false);
    };
    load();
  }, []);
  if (loading) return <div className="text-center py-12 text-gray-400">Loading...</div>;
  if (!stats) return null;
  return (
    <div className="space-y-5">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[
          {label:'Total Projects',  val:fmtN(stats.total),        color:'bg-blue-600 text-white'},
          {label:'In Progress',     val:fmtN(stats.in_progress),  color:'bg-green-600 text-white'},
          {label:'Active Value',    val:fmtCr(stats.active_value),color:'bg-indigo-600 text-white'},
          {label:'Completed',       val:fmtN(stats.completed),    color:'bg-emerald-600 text-white'},
        ].map(c=>(
          <div key={c.label} className={`${c.color} rounded-xl p-4 text-center`}>
            <p className="text-2xl font-bold">{c.val}</p>
            <p className="text-xs font-medium mt-1 opacity-90">{c.label}</p>
          </div>
        ))}
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div className="bg-white rounded-xl border border-gray-200 p-5">
          <h3 className="text-sm font-bold text-gray-700 mb-3">Status Pipeline</h3>
          {[
            {label:'Enquiry',    val:stats.enquiry},
            {label:'Estimation', val:stats.estimation},
            {label:'Agreement',  val:stats.agreement},
            {label:'In Progress',val:stats.in_progress},
            {label:'Completed',  val:stats.completed},
            {label:'Handover',   val:stats.handover},
            {label:'On Hold',    val:stats.on_hold},
            {label:'Cancelled',  val:stats.cancelled},
          ].map(s=>(
            <div key={s.label} className="flex items-center gap-3 mb-1.5">
              <span className="text-xs text-gray-600 w-24">{s.label}</span>
              <div className="flex-1 bg-gray-100 rounded-full h-2">
                <div className="bg-blue-500 h-2 rounded-full" style={{width:`${stats.total>0?(Number(s.val)/Number(stats.total))*100:0}%`}}/>
              </div>
              <span className="text-xs font-semibold text-gray-700 w-6 text-right">{fmtN(s.val)}</span>
            </div>
          ))}
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-5">
          <h3 className="text-sm font-bold text-gray-700 mb-3">By Building Type</h3>
          {[
            {label:'Independent House', val:stats.ih_count,  color:'bg-blue-500'},
            {label:'Villa',             val:stats.vi_count,  color:'bg-purple-500'},
            {label:'Apartments',        val:stats.apt_count, color:'bg-cyan-500'},
          ].map(s=>(
            <div key={s.label} className="flex items-center gap-3 mb-2">
              <span className="text-xs text-gray-600 w-36">{s.label}</span>
              <div className="flex-1 bg-gray-100 rounded-full h-2">
                <div className={`${s.color} h-2 rounded-full`} style={{width:`${stats.total>0?(Number(s.val)/Number(stats.total))*100:0}%`}}/>
              </div>
              <span className="text-xs font-semibold text-gray-700 w-6 text-right">{fmtN(s.val)}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

// â”€â”€ Main ProjectPage â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
const ProjectPage = () => {
  const [tab, setTab] = useState<'projects'|'stats'>('projects');
  return (
    <div className="p-6">
      <div className="mb-6">
        <h1 className="text-xl font-bold text-gray-900">Project Master</h1>
        <p className="text-sm text-gray-500 mt-0.5">Manage projects Â· Blocks Â· Floors Â· Budget Â· Activity Templates Â· Health Scores</p>
      </div>
      <div className="flex gap-1 bg-gray-100 p-1 rounded-lg w-fit mb-6">
        {([
          {key:'projects',label:'Projects', icon:<Building2 size={14}/>},
          {key:'stats',   label:'Statistics',icon:<BarChart2 size={14}/>},
        ] as const).map(t=>(
          <button key={t.key} onClick={()=>setTab(t.key)}
            className={`flex items-center gap-2 px-4 py-1.5 text-sm font-medium rounded-md transition-colors
              ${tab===t.key?'bg-white text-gray-900 shadow-sm':'text-gray-500 hover:text-gray-700'}`}>
            {t.icon}{t.label}</button>
        ))}
      </div>
      {tab==='projects' && <ProjectsListTab/>}
      {tab==='stats'    && <StatsTab/>}
    </div>
  );
};

export default ProjectPage;


